import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../utils/firebaseconfig";
import {
  doc, setDoc, updateDoc, onSnapshot, arrayUnion, arrayRemove, increment,
  collection, addDoc, query, orderBy, getDoc, deleteDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "../styles/LiveStreamHost.css";
import { playBellSound } from "../assets/sounds/bell";
import { checkVerification } from "../utils/verificationCheck";

// WebRTC configuration with STUN and TURN servers
const iceServers = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
    { urls: "stun:stun4.l.google.com:19302" },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443?transport=tcp",
      username: "openrelayproject",
      credential: "openrelayproject"
    }
  ],
  iceCandidatePoolSize: 10,
};

function WatchLiveStream() {
  const { id } = useParams();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const remoteStream = useRef(new MediaStream());
  const chatInputRef = useRef(null);

  const [status, setStatus] = useState("Connecting...");
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [user, setUser] = useState(null);
  const [timeLeft, setTimeLeft] = useState("");
  const [isLive, setIsLive] = useState(true);
  const [highestBid, setHighestBid] = useState({ amount: 0, user: "" });
  const [previousHighestBid, setPreviousHighestBid] = useState({ amount: 0, user: "" });
  const [streamInfo, setStreamInfo] = useState({ title: "", host: "", hostId: "" });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (authUser) => {
      setUser(authUser);
      setIsVerified(authUser?.emailVerified || false);
      if (authUser && streamInfo.hostId) {
        checkIfFollowing(authUser.uid, streamInfo.hostId);
      }
    });
    return () => unsub();
  }, [streamInfo.hostId]);

  // Check if the current user is following the host
  const checkIfFollowing = async (userId, hostId) => {
    if (!userId || !hostId) return;
    
    try {
      const userDoc = await getDoc(doc(db, "User", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setIsFollowing(userData.following?.includes(hostId) || false);
      }
    } catch (error) {
      console.error("Error checking follow status:", error);
    }
  };

  // Effect to play sound when highest bid changes
  useEffect(() => {
    // Skip on component initialization
    if (previousHighestBid.amount === 0) {
      setPreviousHighestBid(highestBid);
      return;
    }

    // Play sound if there's a new highest bid and it's not the initial one
    if (highestBid.amount > previousHighestBid.amount && soundEnabled) {
      playBellSound();
    }

    // Update previous highest bid
    setPreviousHighestBid(highestBid);
  }, [highestBid, soundEnabled]);

  // Handle connection state changes for debugging
  const handleConnectionStateChange = (pc) => {
    pc.onconnectionstatechange = (event) => {
      console.log("Connection state changed:", pc.connectionState);
      
      if (pc.connectionState === "connected") {
        setStatus("🔴 Live");
        console.log("WebRTC connection established successfully");
      } else if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
        console.log("Connection failed or disconnected, attempting to reconnect...");
        
        // Only attempt to reconnect a few times to prevent infinite loops
        if (connectionAttempts < 3) {
          setConnectionAttempts(prev => prev + 1);
          setStatus("Reconnecting...");
          // Connection will be re-established by the cleanup and re-run of this effect
        } else {
          setStatus("Connection failed. Please refresh.");
        }
      }
    };
  };

  // Handle ICE connection state changes for debugging
  const handleICEConnectionStateChange = (pc) => {
    pc.oniceconnectionstatechange = (event) => {
      console.log("ICE connection state:", pc.iceConnectionState);
    };
  };

  useEffect(() => {
    console.log("Establishing WebRTC connection with ICE servers...");
    const pc = new RTCPeerConnection(iceServers);

    const viewerId = Math.random().toString(36).substring(2, 9);
    const viewerRef = doc(db, "livestreams", id, "viewers", viewerId);
    const chatRef = collection(db, "livestreams", id, "chat");

    pcRef.current = pc;
    videoRef.current.srcObject = remoteStream.current;

    // Add connection state change handlers
    handleConnectionStateChange(pc);
    handleICEConnectionStateChange(pc);

    pc.ontrack = (event) => {
      console.log("Received track:", event.track.kind);
      event.streams[0].getTracks().forEach((track) => {
        console.log("Adding track to remote stream:", track.kind);
        if (!remoteStream.current.getTracks().some((t) => t.id === track.id)) {
          remoteStream.current.addTrack(track);
        }
      });
      setStatus("🔴 Live");
    };

    setDoc(viewerRef, {});

    const unsubSignal = onSnapshot(viewerRef, async (snap) => {
      const data = snap.data();
      if (!data || pc.signalingState === "closed") return;

      if (data?.offer && !pc.currentRemoteDescription) {
        console.log("Received offer, setting remote description");
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          console.log("Remote description set successfully");
        } catch (error) {
          console.error("Error setting remote description:", error);
        }
      }

      if (pc.signalingState === "have-remote-offer" && pc.localDescription === null) {
        console.log("Creating answer...");
        try {
          const answer = await pc.createAnswer();
          console.log("Answer created, setting local description");
          await pc.setLocalDescription(answer);
          console.log("Local description set, updating viewer ref with answer");
          await updateDoc(viewerRef, {
            answer: { type: answer.type, sdp: answer.sdp },
          });
          console.log("Answer sent successfully");
        } catch (error) {
          console.error("Error during answer creation process:", error);
        }
      }

      if (data?.iceCandidates) {
        console.log(`Received ${data.iceCandidates.length} ICE candidates from host`);
        for (const c of data.iceCandidates) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(c)).catch(console.warn);
          } catch (error) {
            console.warn("Failed to add ICE candidate:", error);
          }
        }
      }
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("Generated ICE candidate:", event.candidate.type);
        updateDoc(viewerRef, {
          iceCandidates: arrayUnion(event.candidate.toJSON()),
        });
      }
    };

    // Handle ICE gathering state changes
    pc.onicegatheringstatechange = (event) => {
      console.log("ICE gathering state:", pc.iceGatheringState);
    };

    const unsubChat = onSnapshot(query(chatRef, orderBy("timestamp")), (snap) => {
      const msgs = snap.docs.map(doc => doc.data());
      setMessages(msgs);

      const numericMsgs = msgs.filter(msg => /^\d+$/.test(msg.text));
      if (numericMsgs.length) {
        const max = numericMsgs.reduce((a, b) => (+b.text > +a.text ? b : a));
        setHighestBid({ amount: max.text, user: max.user });
      }
    });

    const unsubStream = onSnapshot(doc(db, "livestreams", id), (docSnap) => {
      if (!docSnap.exists()) return;
      const data = docSnap.data();
      setIsLive(data.isLive !== false);
      setStreamInfo({ 
        title: data.title, 
        host: data.host,
        hostId: data.ownerID || data.hostId  // Use whichever field contains the host's user ID
      });

      if (user && data.ownerID) {
        checkIfFollowing(user.uid, data.ownerID);
      }

      const endTime = new Date(data.endTime);
      const interval = setInterval(() => {
        const diff = endTime - new Date();
        if (diff <= 0) {
          clearInterval(interval);
          setTimeLeft("00:00");
          setIsLive(false);
          setTimeout(() => navigate("/live-auction"), 3000);
        } else {
          const min = Math.floor(diff / 60000);
          const sec = Math.floor((diff % 60000) / 1000);
          setTimeLeft(`${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`);
        }
      }, 1000);

      return () => clearInterval(interval);
    });

    return () => {
      console.log("Cleaning up WebRTC connection");
      pc.close();
      remoteStream.current.getTracks().forEach((track) => track.stop());
      unsubSignal();
      unsubChat();
      unsubStream();
    };
  }, [id, navigate, user, connectionAttempts]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || !user) return;
    
    // Check if the message is a bid (numeric value)
    const isBid = /^\d+$/.test(chatInput.trim());
    
    // If this is a bid, verify email verification status
    if (isBid && !isVerified) {
      if (!checkVerification(navigate)) {
        return;
      }
    }
    
    try {
      await addDoc(collection(db, "livestreams", id, "chat"), {
        text: chatInput,
        user: user.displayName || user.email,
        timestamp: new Date(),
      });
      setChatInput("");
      chatInputRef.current?.focus();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  const toggleFollow = async () => {
    if (!user || !streamInfo.hostId || user.uid === streamInfo.hostId) {
      return; // Can't follow if not logged in, host not identified, or trying to follow yourself
    }

    try {
      setFollowingLoading(true);
      const userRef = doc(db, "User", user.uid);
      const hostRef = doc(db, "User", streamInfo.hostId);
      const followerRef = doc(db, "User", streamInfo.hostId, "Followers", user.uid);

      if (isFollowing) {
        // Unfollow: Remove host from user's following array and decrement host's follower count
        await updateDoc(userRef, {
          following: arrayRemove(streamInfo.hostId)
        });
        
        // Delete the follower document
        await deleteDoc(followerRef);
        
        // Update the host's followers count
        await updateDoc(hostRef, {
          followersCount: increment(-1)
        });
      } else {
        // Follow: Add host to user's following array and increment host's follower count
        await updateDoc(userRef, {
          following: arrayUnion(streamInfo.hostId)
        });
        
        // Create a follower document
        await setDoc(followerRef, {
          uid: user.uid,
          username: user.displayName || user.email,
          timestamp: new Date()
        });
        
        // Update the host's followers count
        await updateDoc(hostRef, {
          followersCount: increment(1)
        });
      }
      
      // Update local state
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.error("Error toggling follow:", error);
    } finally {
      setFollowingLoading(false);
    }
  };

  // Function to manually refresh the connection
  const refreshConnection = () => {
    setConnectionAttempts(0);
    setStatus("Reconnecting...");
    // The cleanup and re-run of the main effect will handle reconnection
  };

  return (
    <div className="live-stream-host">
      <div className="stream-header">
        <h2>{streamInfo.title || "Live Stream"}</h2>
        <p className="host-info">Hosted by: {streamInfo.host}</p>
      </div>

      <div className="stream-container">
        <div className="video-section">
          <div className="stream-info">
            <p>🎥 Status: <strong>{status}</strong></p>
            <p>⏰ Time Left: <strong>{timeLeft}</strong></p>
            <div className="stream-actions">
              <button 
                onClick={toggleSound} 
                className="sound-toggle"
                title={soundEnabled ? "Mute notification sounds" : "Enable notification sounds"}
              >
                {soundEnabled ? "🔔" : "🔕"}
              </button>
              {user && user.uid !== streamInfo.hostId && (
                <button 
                  onClick={toggleFollow} 
                  className={`follow-toggle ${isFollowing ? 'following' : ''}`}
                  disabled={followingLoading}
                  title={isFollowing ? "Unfollow host" : "Follow host"}
                >
                  {followingLoading ? "..." : isFollowing ? "★" : "☆"}
                </button>
              )}
            </div>
          </div>
          
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="main-video"
          />

          {status === "Connection failed. Please refresh." && (
            <div className="connection-error">
              <p>🔄 Having trouble connecting? </p>
              <button onClick={refreshConnection} className="refresh-button">
                Refresh Connection
              </button>
            </div>
          )}

          <div className="bid-banner">
            <p>🏆 Highest Bid: <strong>AED {highestBid.amount}</strong> by <strong>{highestBid.user}</strong></p>
          </div>
        </div>

        <div className="chat-section">
          <div className="chat-header">
            <h3>💬 Live Chat</h3>
          </div>

          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div 
                key={i} 
                className={`chat-message ${/^\d+$/.test(msg.text) ? 'bid' : ''}`}
              >
                <strong>{msg.user}:</strong> {msg.text}
              </div>
            ))}
          </div>

          {user && isLive && (
            <form onSubmit={sendMessage} className="chat-input-form">
              <input
                ref={chatInputRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={isVerified ? "Enter your bid or message..." : "Enter message (verify email to bid)"}
                className="chat-input"
              />
              <button type="submit" className="send-button">
                Send
              </button>
            </form>
          )}

          {!isLive && (
            <div className="stream-ended">
              <p>🔴 Stream has ended</p>
              <button onClick={() => navigate("/live-auction")} className="return-button">
                Return to Live Auctions
              </button>
            </div>
          )}
          
          {user && !isVerified && (
            <div className="verification-warning">
              <p>⚠️ Email verification is required to place bids</p>
              <button 
                onClick={() => checkVerification(navigate)}
                className="verify-email-button"
              >
                Verify Email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WatchLiveStream;
